import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { QuickTableChangeDialog } from "@/components/QuickTableChangeDialog";
import { Check, X, Clock, Mail, Edit, Users, Phone, AlertTriangle, ArrowRightLeft, Sparkles, Crown, StickyNote, Baby, MoreVertical, UserX } from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Table, CombinedTable, TableAvailability, TableWeekdayAvailability, Reservation } from "@/types/table";
import { hasInactiveOrMissingTable, hasCapacityExceeded, hasClosureConflict } from "@/lib/reservationProtectionUtils";
import { useServiceTimeCompare } from "@/hooks/useServiceTimeCompare";

interface DashboardReservation {
  id: number;
  supabaseId: string;
  time: string;
  name: string;
  persons: number;
  numberOfChildren: number;
  hasStroller?: boolean;
  tableId: string | undefined;
  combinedTableId: string | undefined;
  status: "planifié" | "arrivé" | "annulé" | "en_attente";
  cancellationReason?: string;
  phone: string;
  email: string | undefined;
  reservationDate: Date;
  createdAt: Date;
  notes: string | undefined;
  internalNotes: string | undefined;
  durationMinutes?: number;
  source?: string;
}

const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
};

interface DashboardListViewProps {
  reservations: DashboardReservation[];
  selectedDate: Date;
  tables: Table[];
  combinedTables: CombinedTable[];
  convertedReservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  closures: any[];
  isMobile: boolean;
  getReservationStatus: (reservation: DashboardReservation) => { type: string; label: string; delay: number; variant: string };
  getStatusBadge: (status: string, cancellationReason?: string) => React.ReactNode;
  getLoyaltyBadge: (email: string | undefined) => React.ReactNode;
  getNoShowBadge: (email: string | undefined) => React.ReactNode;
  getCustomerNotes: (email: string | undefined) => string | null;
  onUpdateStatus: (reservationId: number, status: "arrivé" | "annulé" | "planifié", cancellationReason?: string) => void;
  onEdit: (reservation: DashboardReservation) => void;
  onPreview?: (reservation: DashboardReservation) => void;
  onQuickTableChange: (reservationId: number, tableId: string | null, combinedTableId: string | null) => void;
  onSwapTables?: (aSupabaseId: string, bSupabaseId: string) => Promise<void> | void;
  onSendEmail: (data: { reservationId: number; reservationUuid: string; emailType: 'cancellation' | 'modification' | 'confirmation'; reservationName: string; reservationEmail: string }) => void;
  sendingEmail: number | null;
  reservationDurationMinutes: number;

  reservationSettings?: {
    bookingMode: 'slot' | 'time';
    reservationDurationMinutes: number;
  };
}

export const DashboardListView = ({
  reservations,
  selectedDate,
  tables,
  combinedTables,
  convertedReservations,
  availabilities,
  weekdayAvailabilities,
  closures,
  isMobile,
  getReservationStatus,
  getStatusBadge,
  getLoyaltyBadge,
  getNoShowBadge,
  getCustomerNotes,
  onUpdateStatus,
  onEdit,
  onPreview,
  onQuickTableChange,
  onSwapTables,
  onSendEmail,
  sendingEmail,
  reservationDurationMinutes,
  reservationSettings,

}: DashboardListViewProps) => {
  // Click anywhere on the row/card opens the preview dialog.
  // Buttons, dropdowns, tooltips and links keep their own behavior.
  const handleRowClick = (
    e: React.MouseEvent<HTMLElement>,
    reservation: DashboardReservation,
  ) => {
    if (!onPreview) return;
    const target = e.target as HTMLElement;
    if (
      target.closest(
        'button, a, label, input, select, textarea, [role="menu"], [role="menuitem"], [role="dialog"], [data-state="open"], [data-no-preview]',
      )
    ) {
      return;
    }
    onPreview(reservation);
  };
  const compareServiceTime = useServiceTimeCompare();
  // Flat list sorted by time
  const flatReservations = [...reservations]
    .filter(r => format(r.reservationDate, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"))
    .sort((a, b) => compareServiceTime(a.time, b.time));

  if (flatReservations.length === 0) {
    return (
      <Card className="p-8 text-center text-muted-foreground">
        <p className="text-sm">Aucune réservation pour cette date</p>
      </Card>
    );
  }

  const renderTableCell = (reservation: DashboardReservation) => {
    const hasTableProblem = hasInactiveOrMissingTable(reservation, tables, combinedTables);
    const hasCapacityProblem = hasCapacityExceeded(reservation, tables, combinedTables);

    return (
      <QuickTableChangeDialog
        reservation={{
          id: reservation.id,
          supabaseId: reservation.supabaseId,
          tableId: reservation.tableId,
          combinedTableId: reservation.combinedTableId,
          persons: reservation.persons,
          time: reservation.time,
          reservationDate: reservation.reservationDate,
          customerName: reservation.name,
        }}
        tables={tables}
        combinedTables={combinedTables}
        allReservations={convertedReservations}
        availabilities={availabilities}
        weekdayAvailabilities={weekdayAvailabilities}
        onTableChange={onQuickTableChange}
        onSwapTables={onSwapTables}
        disabled={reservation.status === 'annulé'}
        reservationSettings={reservationSettings}
      >

        <button
          className={cn(
            "flex items-center gap-1 px-2 py-1 rounded hover:bg-muted/80 transition-colors cursor-pointer group",
            hasTableProblem && "text-amber-700 dark:text-amber-400 font-semibold",
            !hasTableProblem && hasCapacityProblem && "text-blue-700 dark:text-blue-400 font-semibold",
            reservation.status === 'annulé' && "opacity-50 cursor-not-allowed"
          )}
          disabled={reservation.status === 'annulé'}
        >
          {reservation.tableId && (() => {
            const table = tables.find(t => t.id === reservation.tableId);
            if (!table) {
              return <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">Table supprimée</Badge>;
            }
            const isExceeded = hasCapacityProblem && reservation.persons > table.capacity;
            return (
              <span>
                {table.name}
                {isExceeded && <span className="text-blue-600 dark:text-blue-400 font-semibold ml-1">({reservation.persons}/{table.capacity})</span>}
              </span>
            );
          })()}
          {reservation.combinedTableId && (() => {
            const ct = combinedTables.find(c => c.id === reservation.combinedTableId);
            if (!ct) {
              return <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">Table combinée supprimée</Badge>;
            }
            const isExceeded = hasCapacityProblem && reservation.persons > ct.totalCapacity;
            return (
              <span>
                {ct.name}
                {isExceeded && <span className="text-blue-600 dark:text-blue-400 font-semibold ml-1">({reservation.persons}/{ct.totalCapacity})</span>}
              </span>
            );
          })()}
          {!reservation.tableId && !reservation.combinedTableId && (
            <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">Aucune table</Badge>
          )}
          {reservation.status !== 'annulé' && (
            <ArrowRightLeft className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground ml-1" />
          )}
        </button>
      </QuickTableChangeDialog>
    );
  };

  const renderStatusBadges = (reservation: DashboardReservation) => {
    const status = getReservationStatus(reservation);
    return (
      <>
        {status.type === "arrived" && <Badge className="bg-green-500 text-white"><span className="lg:hidden">✓</span><span className="hidden lg:inline">✓ {status.label}</span></Badge>}
        {status.type === "no-show" && <Badge className="bg-gray-700 text-white"><span className="lg:hidden">NS</span><span className="hidden lg:inline">No-show</span></Badge>}
        {status.type === "canceled" && <Badge variant="outline" className="text-muted-foreground border-muted-foreground/30 bg-muted/50"><span className="lg:hidden">✕</span><span className="hidden lg:inline">{status.label}</span></Badge>}
        {status.type === "scheduled" && <Badge variant="secondary"><Clock className="w-3 h-3 lg:hidden" /><span className="hidden lg:inline">{status.label}</span></Badge>}
        {status.type === "on-time" && <Badge className="bg-green-500 text-white"><Clock className="w-3 h-3 lg:hidden" /><span className="hidden lg:inline">{status.label}</span></Badge>}
        {status.type === "late" && <Badge className="bg-orange-500 text-white"><span className="lg:hidden">{status.delay} mins</span><span className="hidden lg:inline">{status.label} +{status.delay} min</span></Badge>}
        {status.type === "very-late" && <Badge variant="destructive"><span className="lg:hidden">{status.delay} mins</span><span className="hidden lg:inline">{status.label} +{status.delay} min</span></Badge>}
        {status.type === "no-show-probable" && <Badge variant="destructive" className="bg-gray-700"><span className="lg:hidden">{status.delay} mins</span><span className="hidden lg:inline">{status.label} +{status.delay} min</span></Badge>}
      </>
    );
  };

  const renderNoteIndicators = (reservation: DashboardReservation) => {
    const customerNotes = getCustomerNotes(reservation.email);
    const hasClosureProblem = hasClosureConflict(reservation, closures);

    return (
      <div className="flex items-center gap-1">
        {hasClosureProblem && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              </TooltipTrigger>
              <TooltipContent className="bg-red-50 text-red-950 border-red-200 shadow-lg max-w-xs" side="right">
                <p className="text-sm font-semibold">Réservation pendant une période de fermeture</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {reservation.notes && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-2 h-8 bg-amber-500 rounded-full cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="bg-amber-50 text-amber-950 border-amber-200 shadow-lg max-w-xs" side="right" sideOffset={5}>
                <p className="text-sm font-semibold mb-1">Note spéciale:</p>
                <p className="text-sm">{reservation.notes}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {customerNotes && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <StickyNote className="w-4 h-4 text-violet-500 cursor-help flex-shrink-0" />
              </TooltipTrigger>
              <TooltipContent className="bg-violet-50 text-violet-950 border-violet-200 shadow-lg max-w-xs dark:bg-violet-950 dark:text-violet-100 dark:border-violet-800" side="right" sideOffset={5}>
                <p className="text-sm font-semibold mb-1">Note client:</p>
                <p className="text-sm">{customerNotes}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  };

  const renderActions = (reservation: DashboardReservation) => {
    const status = getReservationStatus(reservation);

    return (
      <div className="flex items-center gap-1">
        {/* Status buttons - always visible */}
        {status.type !== "arrived" && status.type !== "no-show" && (
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => onUpdateStatus(reservation.id, "arrivé")} title="Marquer comme arrivé">
            <Check className="w-4 h-4" />
          </Button>
        )}
        {status.type !== "canceled" && status.type !== "no-show" && (
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => onUpdateStatus(reservation.id, "annulé")} title="Annuler">
            <X className="w-4 h-4" />
          </Button>
        )}
        {(status.type === "late" || status.type === "very-late" || status.type === "no-show-probable") && (
          <Button size="icon" variant="outline" className="h-8 w-8 text-gray-700 border-gray-400 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800" onClick={() => onUpdateStatus(reservation.id, "annulé", "no-show")} title="Marquer no-show">
            <UserX className="w-4 h-4" />
          </Button>
        )}
        {(status.type === "arrived" || status.type === "canceled" || status.type === "no-show") && (
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => onUpdateStatus(reservation.id, "planifié")} title="Marquer comme planifié">
            <Clock className="w-4 h-4" />
          </Button>
        )}
        {/* Secondary actions dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 bg-background z-50 border shadow-lg">
            <DropdownMenuItem onClick={() => onEdit(reservation)}>
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.location.href = `tel:${reservation.phone}`}>
              <Phone className="w-4 h-4 mr-2" />
              Appeler
            </DropdownMenuItem>
            {reservation.email && reservation.status === "annulé" && (
              <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'cancellation', reservationName: reservation.name, reservationEmail: reservation.email! })} disabled={sendingEmail === reservation.id}>
                <Mail className="w-4 h-4 mr-2" />
                Email d'annulation
              </DropdownMenuItem>
            )}
            {reservation.email && reservation.status === "planifié" && (
              <>
                <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'confirmation', reservationName: reservation.name, reservationEmail: reservation.email! })} disabled={sendingEmail === reservation.id}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email de confirmation
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'modification', reservationName: reservation.name, reservationEmail: reservation.email! })} disabled={sendingEmail === reservation.id}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email de modification
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  if (isMobile) {
    const statusBarColor = (reservation: DashboardReservation): string => {
      const status = getReservationStatus(reservation);
      switch (status.type) {
        case "arrived":
        case "on-time":
          return "bg-emerald-500";
        case "late":
          return "bg-orange-500";
        case "very-late":
        case "no-show-probable":
          return "bg-red-500";
        case "canceled":
          return "bg-slate-300 dark:bg-slate-600";
        case "no-show":
          return "bg-gray-700";
        case "scheduled":
        default:
          return "bg-slate-300 dark:bg-slate-600";
      }
    };

    const isTimeMode = reservationSettings?.bookingMode === 'time';

    // Group by HH:MM
    const groups: { time: string; items: DashboardReservation[] }[] = [];
    flatReservations.forEach((r) => {
      const last = groups[groups.length - 1];
      if (last && last.time === r.time) last.items.push(r);
      else groups.push({ time: r.time, items: [r] });
    });

    return (
      <div className="rounded-lg border bg-card overflow-clip">

        {groups.map((group) => (
          <div key={group.time}>
            <div className="sticky top-0 z-10 bg-muted/70 backdrop-blur px-3 py-1 border-b text-[11px] uppercase tracking-wide text-muted-foreground font-medium flex items-center justify-between">
              <span className="tabular-nums">{group.time}</span>
              <span>
                {group.items.length} résa · {group.items.reduce((s, r) => s + r.persons, 0)} pers.
              </span>
            </div>
            {group.items.map((reservation) => {
              const hasTableProblem = hasInactiveOrMissingTable(reservation, tables, combinedTables);
              const hasCapacityProblem = hasCapacityExceeded(reservation, tables, combinedTables);
              const hasClosureProblem = hasClosureConflict(reservation, closures);
              const customerNotes = getCustomerNotes(reservation.email);
              const duration = reservation.durationMinutes ?? reservationDurationMinutes;
              const isCustomDuration = reservation.durationMinutes != null && reservation.durationMinutes !== reservationDurationMinutes;
              const endTime = calculateEndTime(reservation.time, duration);

              const tableLabel = (() => {
                if (reservation.tableId) {
                  const t = tables.find((x) => x.id === reservation.tableId);
                  return t?.name ?? "Table ✕";
                }
                if (reservation.combinedTableId) {
                  const c = combinedTables.find((x) => x.id === reservation.combinedTableId);
                  return c?.name ?? "Combinée ✕";
                }
                return "—";
              })();

              const isCanceled = reservation.status === "annulé";
              const isArrived = reservation.status === "arrivé";
              const status = getReservationStatus(reservation);

              return (
                <div
                  key={reservation.id}
                  id={`reservation-${reservation.id}`}
                  onClick={(e) => handleRowClick(e, reservation)}
                  className={cn(
                    "relative flex items-stretch border-b last:border-b-0 transition-colors",
                    onPreview && "cursor-pointer active:bg-muted/60",
                    isArrived && "opacity-60",
                    isCanceled && "opacity-50",
                    hasClosureProblem && !isArrived && "bg-red-50 dark:bg-red-950/20",
                    !hasClosureProblem && hasTableProblem && !isArrived && "bg-amber-50 dark:bg-amber-950/20",
                    !hasClosureProblem && !hasTableProblem && hasCapacityProblem && !isArrived && "bg-blue-50 dark:bg-blue-950/20"
                  )}
                >
                  <div aria-hidden="true" className={cn("w-[3px] shrink-0", statusBarColor(reservation))} />

                  <div className="flex items-center pl-2.5 shrink-0">
                    <span className="text-sm font-semibold tabular-nums w-[44px] text-foreground">
                      {reservation.time}
                    </span>
                  </div>

                  <div className="flex items-center pl-1 pr-2 shrink-0">
                    <div className="h-7 min-w-7 px-1.5 rounded-full bg-muted flex items-center justify-center text-xs font-semibold tabular-nums">
                      {reservation.persons}
                      {reservation.numberOfChildren > 0 && (
                        <span className="text-[10px] text-muted-foreground ml-0.5">+{Math.min(reservation.numberOfChildren, 9)}{reservation.numberOfChildren > 9 ? '+' : ''}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 py-2 pr-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className={cn("text-sm font-medium truncate min-w-0", isCanceled && "line-through")}>
                        {reservation.name.split(" ")[0]}
                      </span>
                      <span className="text-muted-foreground text-xs shrink-0">·</span>
                      <span
                        className={cn(
                          "text-xs truncate min-w-0",
                          hasTableProblem ? "text-amber-700 dark:text-amber-400 font-medium" :
                          hasCapacityProblem ? "text-blue-700 dark:text-blue-400 font-medium" :
                          "text-muted-foreground"
                        )}
                      >
                        {tableLabel}
                      </span>
                    </div>
                    {(isTimeMode || isCustomDuration) && (
                      <div className="text-[10px] text-muted-foreground tabular-nums mt-0.5">
                        → {endTime}{isCustomDuration && <span className="ml-1">({duration}′)</span>}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 pr-1.5 shrink-0">
                    {reservation.source === 'walk_in' && (
                      <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">WI</span>
                    )}
                    {getLoyaltyBadge(reservation.email)}
                    {getNoShowBadge(reservation.email)}
                    {reservation.hasStroller && <StrollerIcon size={12} className="text-muted-foreground" />}
                    {hasClosureProblem && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                    {reservation.notes && <span role="img" aria-label="Note spéciale" className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                    {customerNotes && <StickyNote aria-label="Note client" className="w-3.5 h-3.5 text-violet-500" />}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 -mr-1"
                          aria-label={`Actions pour ${reservation.name} à ${reservation.time}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                        {status.type !== "arrived" && status.type !== "no-show" && (
                          <DropdownMenuItem onClick={() => onUpdateStatus(reservation.id, "arrivé")}>
                            <Check className="w-4 h-4 mr-2" />
                            Marquer arrivé
                          </DropdownMenuItem>
                        )}
                        {status.type !== "canceled" && status.type !== "no-show" && (
                          <DropdownMenuItem onClick={() => onUpdateStatus(reservation.id, "annulé")}>
                            <X className="w-4 h-4 mr-2" />
                            Annuler
                          </DropdownMenuItem>
                        )}
                        {(status.type === "late" || status.type === "very-late" || status.type === "no-show-probable") && (
                          <DropdownMenuItem onClick={() => onUpdateStatus(reservation.id, "annulé", "no-show")}>
                            <UserX className="w-4 h-4 mr-2" />
                            No-show
                          </DropdownMenuItem>
                        )}
                        {(status.type === "arrived" || status.type === "canceled" || status.type === "no-show") && (
                          <DropdownMenuItem onClick={() => onUpdateStatus(reservation.id, "planifié")}>
                            <Clock className="w-4 h-4 mr-2" />
                            Replanifier
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => onEdit(reservation)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.location.href = `tel:${reservation.phone}`}>
                          <Phone className="w-4 h-4 mr-2" />
                          Appeler
                        </DropdownMenuItem>
                        {reservation.email && (
                          <DropdownMenuItem
                            disabled={sendingEmail === reservation.id}
                            onClick={() => onSendEmail({
                              reservationId: reservation.id,
                              reservationUuid: reservation.supabaseId,
                              emailType: reservation.status === "annulé" ? 'cancellation' : 'confirmation',
                              reservationName: reservation.name,
                              reservationEmail: reservation.email!
                            })}
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            {sendingEmail === reservation.id ? 'Envoi…' : 'Envoyer email'}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  // Desktop: table view
  return (
    <Card className="p-3 md:p-4 lg:p-5">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-1 md:px-2 font-semibold text-xs md:text-sm w-8"></th>
              <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Heure</th>
              <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Fin</th>
              <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Nom</th>
              <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Pers.</th>
              <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Table</th>
              <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm hidden lg:table-cell">Créée</th>
              <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Statut</th>
              <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {flatReservations.map((reservation) => {
              const hasTableProblem = hasInactiveOrMissingTable(reservation, tables, combinedTables);
              const hasCapacityProblem = hasCapacityExceeded(reservation, tables, combinedTables);
              const hasClosureProblem = hasClosureConflict(reservation, closures);

              return (
                <tr
                  key={reservation.id}
                  id={`reservation-${reservation.id}`}
                  onClick={(e) => handleRowClick(e, reservation)}
                  className={cn(
                    "border-b hover:bg-muted/50 transition-all duration-300",
                    onPreview && "cursor-pointer",
                    reservation.status === "arrivé" && "opacity-50",
                    reservation.status === "annulé" && "opacity-50 text-muted-foreground",
                    hasClosureProblem && reservation.status !== "arrivé" && "bg-red-50 dark:bg-red-950/20",
                    !hasClosureProblem && hasTableProblem && reservation.status !== "arrivé" && "bg-amber-50 dark:bg-amber-950/20",
                    !hasClosureProblem && !hasTableProblem && hasCapacityProblem && reservation.status !== "arrivé" && "bg-blue-50 dark:bg-blue-950/20"
                  )}
                >
                  <td className="py-2 px-1 md:px-2">{renderNoteIndicators(reservation)}</td>
                  <td className="py-2 px-1.5 md:px-2 lg:px-3 font-medium text-xs md:text-sm">{reservation.time}</td>
                  <td className="py-2 px-1.5 md:px-2 lg:px-3 text-xs md:text-sm">
                    {(() => {
                      const duration = reservation.durationMinutes ?? reservationDurationMinutes;
                      const endTime = calculateEndTime(reservation.time, duration);
                      const isCustom = reservation.durationMinutes != null && reservation.durationMinutes !== reservationDurationMinutes;
                      return (
                        <span className={isCustom ? "text-primary font-medium" : "text-muted-foreground"}>
                          {endTime}
                          {isCustom && <span className="ml-1 text-xs">({duration}′)</span>}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="py-2 px-1.5 md:px-2 lg:px-3 text-xs md:text-sm">
                    <div className="flex items-center">
                      {reservation.notes ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={cn("cursor-help underline decoration-dotted decoration-muted-foreground/50", reservation.status === "annulé" && "line-through")}>{reservation.name}</span>
                            </TooltipTrigger>
                            <TooltipContent className="bg-amber-50 text-amber-950 border-amber-200 shadow-lg max-w-xs" side="top" sideOffset={5}>
                              <p className="text-sm">{reservation.notes}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className={cn(reservation.status === "annulé" && "line-through")}>{reservation.name}</span>
                      )}
                      {reservation.source === 'walk_in' && <Badge variant="warning" className="text-[10px] px-1.5 py-0 ml-1">Walk-in</Badge>}
                      {getLoyaltyBadge(reservation.email)}
                      {getNoShowBadge(reservation.email)}
                    </div>
                  </td>
                  <td className="py-2 px-1.5 md:px-2 lg:px-3 text-xs md:text-sm">
                    <div className="flex items-center gap-1">
                      {reservation.persons}
                      {reservation.numberOfChildren > 0 && (
                        <span className="flex items-center gap-0.5 text-muted-foreground text-xs">
                          <Baby className="w-3 h-3" />
                          +{reservation.numberOfChildren}
                        </span>
                      )}
                      {reservation.hasStroller && (
                        <StrollerIcon size={12} className="text-muted-foreground" />
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-1.5 md:px-2 lg:px-3 text-xs md:text-sm">{renderTableCell(reservation)}</td>
                  <td className="py-2 px-1.5 md:px-2 lg:px-3 text-xs text-muted-foreground hidden lg:table-cell">{format(reservation.createdAt, "d MMM", { locale: fr })}</td>
                  <td className="py-2 px-1.5 md:px-2 lg:px-3">{renderStatusBadges(reservation)}</td>
                  <td className="py-2 px-1.5 md:px-2 lg:px-3">{renderActions(reservation)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
